import type { ReactNode } from "react";
import type { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";

/** Subset of react-data-table column fields used by this app. */
export type RdtLikeColumn = {
  name: string;
  /** API field name for `fields=` projection on list reads. Falls back to selector parsing. */
  field?: string;
  selector?: (row: any) => unknown;
  cell?: (row: any) => ReactNode;
  sortable?: boolean;
  /** Used as minimum width; columns share remaining space equally (`flex: 1`). */
  minWidth?: string | number;
  /** If set without minWidth, contributes to the column minimum; does not fix pixel width. */
  width?: string | number;
  /** @deprecated Ignored — all data columns use equal flex. */
  grow?: number;
  wrap?: boolean;
};

function parsePx(value: string | number | undefined): number | undefined {
  if (value == null) return undefined;
  if (typeof value === "number") return value;
  const n = parseInt(String(value).replace(/[^\d]/g, ""), 10);
  return Number.isFinite(n) ? n : undefined;
}

/**
 * Maps legacy column definitions to MUI DataGrid columns.
 * When `cell` is set, it receives the full row (same as RDT `cell(row)`).
 */
export function rdtLikeToDataGridColumns(
  cols: RdtLikeColumn[],
  idPrefix = "c",
): GridColDef[] {
  return cols.map((col, index) => {
    const field = `${idPrefix}_${index}`;
    const explicitMin = parsePx(col.minWidth);
    const explicitW = parsePx(col.width);
    /** Floor so very short headers still get a touch of space; flex shares the rest equally */
    const minW = Math.max(explicitMin ?? 0, explicitW ?? 0, 80);

    return {
      field,
      headerName: col.name,
      minWidth: minW,
      flex: 1,
      sortable: col.sortable !== false,
      align: "left",
      headerAlign: "left",
      disableColumnMenu: true,
      valueGetter: col.selector
        ? (_value, row) => {
            try {
              const v = col.selector!(row);
              if (v != null && typeof v === "object") return "";
              return v as string | number | boolean | null | undefined;
            } catch {
              return "";
            }
          }
        : undefined,
      renderCell: col.cell
        ? (params: GridRenderCellParams) => <>{col.cell!(params.row)}</>
        : undefined,
    };
  });
}
